import React from 'react';
import { useIntl } from 'react-intl';
// Core
import { Grid } from '@mui/material';
// Components
import { ToggleSwitch, Button } from 'Components';
// Utilities
import {
  getFeatureDisplayCode,
  isParentEnabled,
  disableChildModules,
} from './hierarchicalFeatures';
import {
  isSingleSelectionModule,
  applyDefaultSingleSelection,
} from './singleSelectionModules';
//Style
import './styles.scss';

const Modules = (props) => {
  //Props
  const {
    modulesList,
    moduleSelected,
    savePermissions
  } = props;

  const { messages } = useIntl();

  /**
   * Handles toggling a module's checked state
   * When unchecking, also unchecks any dependent child modules
   * When checking, only auto-checks truly independent features (no :: in code)
   * Features with ::parent or ::! stay unchecked (user must manually enable)
   * Single-selection modules (see singleSelectionModules.js) only check their default subModule
   */
  const handleToggleChange = (checked, index) => {
    let resultList = [...modulesList];
    const module = resultList[index];

    resultList[index].checked = checked;

    if (checked && isSingleSelectionModule(module)) {
      // Single-selection modules (e.g. coachsso): only the default subModule is checked
      resultList[index].subModules = applyDefaultSingleSelection(module);
    } else {
      // Update subModules based on parent state
      resultList[index].subModules?.forEach((subModule) => {
        if (checked) {
          // When enabling: only auto-check truly independent features (no :: at all)
          // Features with ::parent or ::! should stay unchecked
          const hasHierarchy = subModule.code.includes('::');
          subModule.checked = !hasHierarchy;
        } else {
          // When disabling: uncheck all subModules
          subModule.checked = false;
        }
      });
    }

    // If unchecking, also disable any child modules that depend on this one
    if (!checked) {
      disableChildModules(module, resultList);
    }

    savePermissions(resultList);
  };

  /**
   * Determines if a module's toggle should be disabled
   * Disabled if: userCode is 0 (always-on) OR parent dependency not satisfied
   */
  const isToggleDisabled = (item) => {
    // Always-on modules (like Maps) cannot be toggled
    if (item.userCode === 0) return true;

    // If parent dependency exists and parent is not checked, disable this toggle
    if (!isParentEnabled(item, modulesList)) return true;

    return false;
  };

  /**
   * Gets the display code for localization lookup
   * Strips the @parent suffix if present
   */
  const getDisplayCode = (item) => {
    return getFeatureDisplayCode(item.code);
  };

  return (
    <Grid
      container
      justifyContent={'center'}
      direction={'column'}
      spacing={2}
    >
      {
        modulesList?.map((item, index) =>
          <div
            key={`permission-${index}`}
            className="box-checkbox"
          >
            <div className="label-permission">
              <label style={{fontWeight: 400, fontSize: '16px'}}>
                {messages[`role_${getDisplayCode(item)}`] || item.name}
              </label>
            </div>
            <div style={{display: 'flex'}}>
              {
                item.subModules &&
                  <Button
                    type="icon"
                    icon={'settings'}
                    placement="bottom"
                    className="btn-xs"
                    fontSize="inherit"
                    onClick={(e) => {
                      e.preventDefault();
                      moduleSelected({item, index});
                    }}
                  />
              }
              <ToggleSwitch
                id={item.name}
                large
                blueToggle
                optionLabels={[messages['permissions_allow'], messages['permissions_allow']]}
                checked={item.checked}
                disabled={isToggleDisabled(item)}
                onChange={(checked) => handleToggleChange(checked, index)}
              />
            </div>
          </div>
      )}
    </Grid>
  )
}

export default Modules;